
import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import { BarChart3, TrendingUp, Users, FileText, Calendar, Package, Truck, Factory } from 'lucide-react';
import ProcurementPerformanceReport from '@/components/reports/ProcurementPerformanceReport';
import SupplierPerformanceReport from '@/components/reports/SupplierPerformanceReport';
import SupplySourceReport from '@/components/reports/SupplySourceReport';
import { useAuth } from '@/contexts/AuthContext';

const Reports = () => {
  const navigate = useNavigate();
  const { userRole } = useAuth();

  // Check if user has access to procurement reports
  const hasProcurementAccess = ['admin', 'finance', 'seller', 'warehouse'].includes(userRole || '');

  return (
    <Layout fullWidth>
      <div className="h-full">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Reports</h1>
          <p className="text-muted-foreground">Generate and view comprehensive system reports</p>
        </div>
        
        <Tabs defaultValue="procurement" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            {hasProcurementAccess && (
              <TabsTrigger value="procurement" className="gap-2">
                <Package className="h-4 w-4" />
                Procurement Reports
              </TabsTrigger>
            )}
            <TabsTrigger value="price-analytics" className="gap-2">
              <TrendingUp className="h-4 w-4" />
              Price Analytics
            </TabsTrigger>
            <TabsTrigger value="other-reports" className="gap-2">
              <Calendar className="h-4 w-4" />
              Other Reports
            </TabsTrigger>
          </TabsList>

          {hasProcurementAccess && (
            <TabsContent value="procurement" className="space-y-4">
              <Tabs defaultValue="performance" className="space-y-4">
                <TabsList>
                  <TabsTrigger value="performance" className="gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Procurement Performance
                  </TabsTrigger>
                  <TabsTrigger value="suppliers" className="gap-2">
                    <Truck className="h-4 w-4" />
                    Supplier Performance
                  </TabsTrigger>
                  <TabsTrigger value="supply-sources" className="gap-2">
                    <Factory className="h-4 w-4" />
                    Supply Source Reports
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="performance">
                  <ProcurementPerformanceReport />
                </TabsContent>

                <TabsContent value="suppliers">
                  <SupplierPerformanceReport />
                </TabsContent>

                <TabsContent value="supply-sources">
                  <SupplySourceReport />
                </TabsContent>
              </Tabs>
            </TabsContent>
          )}

          <TabsContent value="price-analytics" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-green-100 text-green-700">
                    <TrendingUp className="h-6 w-6" />
                  </div>
                  Price Analytics & Benchmarking
                </CardTitle>
                <CardDescription>
                  Analyze price trends, market benchmarks, and comparative pricing data
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-muted-foreground">
                    Access comprehensive price analysis tools, benchmark reports, and market trend insights.
                  </p>
                  <Button onClick={() => navigate('/price-benchmarking')} className="gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Open Price Analytics Dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="other-reports" className="space-y-4">
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="cursor-pointer hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg bg-purple-100 text-purple-700">
                      <Calendar className="h-6 w-6" />
                    </div>
                    <CardTitle className="text-lg">Harvest Reports</CardTitle>
                  </div>
                  <CardDescription>Track harvest schedules, progress, and yield analytics</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={() => navigate('/harvests')}
                    className="w-full"
                    variant="outline"
                  >
                    View Harvest Data
                  </Button>
                </CardContent>
              </Card>

              <Card className="cursor-pointer hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg bg-orange-100 text-orange-700">
                      <Users className="h-6 w-6" />
                    </div>
                    <CardTitle className="text-lg">User Activity Reports</CardTitle>
                  </div>
                  <CardDescription>Monitor user engagement and system usage statistics</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    disabled
                    className="w-full"
                    variant="outline"
                  >
                    Coming Soon
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Reports;
